// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Organization, organizationResponseSchema } from '@/account/api/types';
import { APIKeyList } from '@/account/components/api-key-list';
import { ClassifyIntegrationDataCard } from '@/account/components/classify-integration-data-card';
import { CRMList } from '@/account/components/crm-list';
import {
  OrganizationAPIKeyAuditList,
  OrganizationCRMAuditList,
} from '@/account/components/organization-audit-list';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { AuditCard } from '@/components/audit-card';
import { AuditDrawer } from '@/components/audit-drawer';
import { LoadingIndicator } from '@/components/navigation-layout';
import { PageContent } from '@/components/page-content';
import { PageLayout } from '@/components/page-layout';
import { useDisclosure } from '@/hooks/use-disclosure';
import { hasIntegration } from '@/utilities/integration';
import { ONE_MINUTE_MS } from '@/utilities/time-constants';
import { Box, Stack } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

export const Route = createFileRoute('/_app/integrations/')({
  component: IntegrationsRoute,
});

interface Props {
  organization: Organization;
}

function IntegrationsContent(props: Props) {
  const crmAuditDisclosure = useDisclosure();
  const apiKeyAuditDisclosure = useDisclosure();
  return (
    <>
      <AuditDrawer
        auditLog={
          <OrganizationAPIKeyAuditList
            organizationKey={props.organization.key}
          />
        }
        disclosure={apiKeyAuditDisclosure}
      />
      <AuditDrawer
        auditLog={
          <OrganizationCRMAuditList organizationKey={props.organization.key} />
        }
        disclosure={crmAuditDisclosure}
      />
      <Stack spacing={3}>
        <AuditCard
          title={'API keys'}
          subtitle={
            'Secure access keys to build custom integrations between your systems and the Hardfin platform'
          }
          onClick={apiKeyAuditDisclosure.open}
        >
          <Box sx={{ width: '100%', paddingY: 4, paddingX: 2 }}>
            <APIKeyList organization={props.organization} />
          </Box>
        </AuditCard>
        <AuditCard
          title={'Enabled integrations'}
          subtitle={
            'A direct integration to pull Customer and Project data from your CRM or ERP system into Hardfin automatically'
          }
          onClick={crmAuditDisclosure.open}
        >
          <CRMList organization={props.organization} />
        </AuditCard>
        {(hasIntegration(props.organization.enabledIntegrations, 'NETSUITE') ||
          hasIntegration(props.organization.enabledIntegrations, 'QBO')) && (
          <Suspense fallback={<LoadingIndicator />}>
            <ClassifyIntegrationDataCard />
          </Suspense>
        )}
      </Stack>
    </>
  );
}

function IntegrationsRoute() {
  const { user } = useCurrentUser();
  const { data } = useSuspenseQuery({
    queryKey: ['organization', user.organization.key],
    refetchInterval: ONE_MINUTE_MS,
    queryFn: async () => {
      const url = `/v1/organization/${user.organization.key}`;
      const response = await api.get(url);
      const result = await parseResponse(
        organizationResponseSchema,
        response.data,
        { schemaName: 'Organization', endpoint: url }
      );
      return result.data;
    },
  });

  return (
    <PageLayout title="Integrations">
      <PageContent>
        <Suspense fallback={<LoadingIndicator />}>
          <IntegrationsContent organization={data} />
        </Suspense>
      </PageContent>
    </PageLayout>
  );
}
